import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { ButtonOutline } from "@/components/common/StandardButton";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { customerOrderColumns } from "@/components/customers/customerOrderColumns";
import { ButtonPrimary } from "@/components/common/StandardButton";
import { Download as DownloadIcon } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@/store/store";
import { fetchCustomerRequest } from "@/store/slices/customer.slice";
import CustomerInformationCard from "@/components/customers/CustomerInformationCard";
import CustomerNotesCard from "@/components/customers/CustomerNotesCard";
import CustomerAddressCard from "@/components/customers/CustomerAddressCard";

export default function CustomerDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customerId = params?.customerId as string;
  const storeType = params?.storeType as string;
  const storeId = params?.storeId as string;

  const { currentCustomer: customer, customerLoading, customerError } = useSelector((state: RootState) => state.customers);

  const [orders, _setOrders] = useState<any[]>([]);
  const [ordersLoading, _setOrdersLoading] = useState(false);
  const [ordersError, _setOrdersError] = useState<string | null>(null);
  const [ordersPagination, _setOrdersPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const [ordersPageIndex, setOrdersPageIndex] = useState(0);
  const [ordersSearchQuery, setOrdersSearchQuery] = useState("");

  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerRequest({ customerId }));
    }
  }, [customerId, dispatch]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleOrdersSearch = (query: string) => {
    setOrdersSearchQuery(query);
    setOrdersPageIndex(0);
  };

  const handleOrdersPageChange = (newPageIndex: number) => {
    setOrdersPageIndex(newPageIndex);
  };

  const handleExportOrders = () => {
    console.log("Export customer orders");
  };

  const customerName = customer
    ? `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Unknown Customer"
    : "Customer";

  const breadcrumbItems = [
    { label: "Customers", href: `/${storeType}/${storeId}/customers` },
    { label: customerName, isActive: true },
  ];

  const ordersActionButtons = [
    { label: "Export Orders", icon: <DownloadIcon className="h-4 w-4" />, onClick: handleExportOrders, variant: "outline" as const },
  ];

  return (
    <div className="view min-h-screen bg-background gap-2.5 flex flex-col">
      <Breadcrumb items={breadcrumbItems} showBackButton={false} />

      <div className="border-b border-border bg-card rounded-lg">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <ButtonOutline onClick={handleBackClick} className="flex items-center gap-2 px-3">
              <ArrowLeft className="h-4 w-4" />
              Back
            </ButtonOutline>

            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{customerName}</h1>
                <p className="text-sm text-muted-foreground">Customer #{customerId}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ButtonOutline size="sm" className="text-xs">Edit Customer</ButtonOutline>
            <ButtonPrimary size="sm" className="text-xs">Create Order</ButtonPrimary>
          </div>
        </div>
      </div>

      <div className="flex gap-2.5 flex-1">
        <div className="w-80 flex flex-col gap-2.5">
          <CustomerInformationCard customer={customer} loading={customerLoading} error={customerError?.message} />
          {customer && <CustomerNotesCard customerId={customer.id} />}
          {customer && <CustomerAddressCard customerId={customer.id} />}
        </div>

        <div className="flex-1 flex flex-col gap-2.5">
          <TableContainer>
            <div className="bg-secondary-background p-[18px] border rounded-[16px]">
              <DataView
                columns={customerOrderColumns}
                data={orders}
                pagination={ordersPagination}
                pageIndex={ordersPageIndex}
                onPageChange={handleOrdersPageChange}
                loading={ordersLoading}
                error={ordersError}
                title="Customer Orders"
                description="View all orders placed by this customer"
                searchPlaceholder="Search orders..."
                searchQuery={ordersSearchQuery}
                onSearch={handleOrdersSearch}
                enableColumnResizing={true}
                enableRowSelection={false}
                emptyMessage="No orders found for this customer."
                loadingMessage="Loading orders..."
                actionButtons={ordersActionButtons}
              />
            </div>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}
